import * as _ from 'lodash';

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, publishLast, refCount } from 'rxjs/operators';

import { BaseService } from './base.service';
import { SeatInvitation } from 'src/app/members/invitation/seatInvitation';
import { ApiVersions } from './api-version';
import { PersonForMemberRequests } from 'src/app/people/models/personForApplication';
import { download } from '../utils/download-file';
import { SubmitApplicationResultModel } from '../models/application/submitApplicationResultModel';
import { IApplicationSubmit } from '../models/application/applicationSubmit';

@Injectable()
export class MembersService extends BaseService {
  private basePath = 'members';
  public readonly seatInvitationFetched = new Subject<SeatInvitation>();
  public readonly personFetched = new Subject<PersonForMemberRequests>();

  constructor() {
    super();
  }

  public readonly workspaceLogoUrl: BehaviorSubject<string> = new BehaviorSubject<string>(
    undefined
  );

  public readonly workspaceHelpLink: BehaviorSubject<string> = new BehaviorSubject<string>(
    undefined
  );

  setWorkspaceLogoUrl(url: string) {
    this.workspaceLogoUrl.next(url);
  }

  setWorkspaceHelpLink(url: string) {
    this.workspaceHelpLink.next(url);
  }

  requestApplication(formId: string, email: string): Observable<any> {
    let payload = JSON.stringify({ email: email, formId: formId });
    return this.post(this.getPath('applications/request'), payload, null, ApiVersions.v0_1, true);
  }

  verifyVerificationCode(applicationId: string, code: string): Observable<any> {
    let payload = JSON.stringify({ applicationId: applicationId, verificationCode: code });
    return this.post(
      this.getPath('applications/verifyVerificationCode'),
      payload,
      null,
      ApiVersions.v0_1,
      true
    );
  }

  requestVerificationCode(applicationId: string, email: string): Observable<any> {
    return this.get(
      this.getPath(
        `applications/requestVerificationCode?applicationId=${applicationId}&email=${email}`
      ),
      null,
      ApiVersions.v0_1,
      true
    );
  }

  requestForOpportunity(
    formId: string,
    email: string,
    listingId: string,
    opportunityBoardId: string
  ): Observable<string> {
    let payload = JSON.stringify({
      email: email,
      formId: formId,
      listingId: listingId,
      opportunityBoardId: opportunityBoardId,
    });
    return this.post(
      this.getPath('applications/opportunity/request'),
      payload,
      null,
      ApiVersions.v0_1,
      true
    );
  }

  getSeatInvitation(personId: string, seatId: string): Observable<SeatInvitation> {
    let path = personId.concat(`/seats/${seatId}`);
    this.getPerson(personId).subscribe((_) => {});

    return this.get(this.getPath(path)).pipe(map(this.mapSeatInvitation.bind(this)));
  }

  sendInvitation(
    personId: string,
    teamId: string,
    seatId: string,
    subject: string,
    message: string,
    reminderDaysFromNow?: number,
    contactName?: string,
    contactEmail?: string
  ): Observable<any> {
    const path = personId.concat(`/teams/${teamId}/seats/${seatId}/invite`);
    const email = {
      subject: subject,
      content: message,
      contactName: contactName,
      contactEmail: contactEmail,
    };
    const model = {
      emailCustomization: email,
      reminderDaysFromNow: reminderDaysFromNow,
    };
    return this.patch(this.getPath(path), model);
  }

  sendInvitationReminder(
    personId: string,
    teamId: string,
    seatId: string,
    subject: string,
    message: string,
    contactName: string,
    contactEmail: string
  ): Observable<void> {
    const path = personId.concat(`/teams/${teamId}/seats/${seatId}/remind`);
    const email = {
      subject: subject,
      content: message,
      contactName: contactName,
      contactEmail: contactEmail,
    };
    return this.post(this.getPath(path), email, null, ApiVersions.v0_1, true);
  }

  cancelInvitationReminder(personId: string, reminderId: string): Observable<any> {
    const path = personId.concat(`/reminders/${reminderId}`);
    return this.delete(this.getPath(path));
  }

  acceptInvitation(personId: string, teamId: string, seatId: string) {
    let path = personId.concat(`/teams/${teamId}/seats/${seatId}/accept`);
    return this.patch(this.getPath(path));
  }

  declineInvitation(
    personId: string,
    teamId: string,
    seatId: string,
    declineReason: string = null
  ) {
    let path = personId.concat(`/teams/${teamId}/seats/${seatId}/decline`);
    if (declineReason) {
      const model = {
        declineReason,
      };
      return this.patch(this.getPath(path), model);
    }
    return this.patch(this.getPath(path));
  }

  rescindInvitation(personId: string, teamId: string, seatId: string) {
    let path = personId.concat(`/teams/${teamId}/seats/${seatId}/uninvite`);
    return this.patch(this.getPath(path));
  }

  submitApplication = async (
    secureLinkId: string,
    applicationSubmit: IApplicationSubmit
  ): Promise<SubmitApplicationResultModel> =>
    await this.post(
      this.getPath(secureLinkId),
      JSON.stringify(applicationSubmit),
      null,
      ApiVersions.v0_1,
      true
    ).toPromise();

  getPerson(personId: string, secureLinkId: string = null): Observable<PersonForMemberRequests> {
    let path = this.getPath(personId);
    if (secureLinkId) {
      path = path.concat('/', secureLinkId);
    }
    return this.get(path)
      .pipe(map(this.convertPerson.bind(this)))
      .pipe(publishLast())
      .pipe(refCount());
  }

  canUseSecureLink(userId: string, secureLinkId: string): Observable<boolean> {
    return this.get(this.getCanUseSecureLink(userId, secureLinkId));
  }

  canUseAnonymousSecureLink(secureLinkId: string): Observable<boolean> {
    let path = this.getPath(`canUseSecureLink/${secureLinkId}`);
    return this.get(path);
  }

  downloadFile(requestUrl: string, fileName: string) {
    const encodedFileName = encodeURIComponent(fileName);
    const path = this.getPath(requestUrl).concat(`?fileName=${encodedFileName}`);
    this.getFile(path).subscribe((res) => {
      download(res, fileName);
    });
  }

  private getPath(path) {
    return `${this.basePath}/${path}`;
  }

  private getCanUseSecureLink(userId: string, secureLinkId: string) {
    return this.getPath(`${userId}/canUseSecureLink/${secureLinkId}`);
  }

  private mapSeatInvitation(data) {
    const seatInvitation = new SeatInvitation(data);
    this.seatInvitationFetched.next(seatInvitation);

    return seatInvitation;
  }

  private convertPerson = (data): PersonForMemberRequests => {
    const person = new PersonForMemberRequests(data);
    this.personFetched.next(person);

    return person;
  };
}
